/**
 * Draw a viewport-aware grid onto a Fabric.js render context.
 *
 * Computes the visible canvas-space bounds from the current viewportTransform
 * (zoom + pan) and tiles grid lines across them, so the grid always fills the
 * visible surface and stays continuous at any zoom level. Line width is scaled
 * so lines stay 1px on screen regardless of zoom.
 *
 * Intended to be called from a Fabric.js `before:render` handler (draws behind
 * all objects). The canvas must NOT have a Fabric `backgroundColor` set —
 * Fabric paints the background after `before:render` and would cover the grid.
 * Provide the backdrop via CSS (on the canvas element or an ancestor) instead.
 *
 * @param {CanvasRenderingContext2D} ctx     - Fabric render context (from the event)
 * @param {import('fabric').Canvas}  canvas   - Fabric.js Canvas instance
 * @param {Object}                   options
 * @param {number}                   options.gridSize            - Grid cell size (canvas units)
 * @param {string}                   [options.gridColor='#E5E7EB']
 * @param {number}                   [options.gridOpacity=0.5]
 */
export function drawViewportGrid(
  ctx,
  canvas,
  { gridSize, gridColor = '#E5E7EB', gridOpacity = 0.5 } = {},
) {
  if (!ctx || !canvas || !gridSize) return;

  const vpt = canvas.viewportTransform;
  if (!vpt) return;
  const zoom = vpt[0]; // scaleX = scaleY = zoom
  const w = canvas.getWidth();
  const h = canvas.getHeight();

  // Fabric skaliert den Backing-Store um devicePixelRatio (enableRetinaScaling);
  // ctx.setTransform unten ERSETZT diese Skalierung. Ohne den Faktor wird das
  // Grid auf High-DPI-Geräten (Smartphone: DPR 3) nur auf 1/DPR der Fläche
  // gezeichnet — sichtbar als Grid, das in der linken oberen Ecke endet.
  const retina =
    typeof canvas.getRetinaScaling === 'function' ? canvas.getRetinaScaling() : 1;

  // Visible bounds in canvas-space coordinates
  const left = -vpt[4] / zoom;
  const top = -vpt[5] / zoom;
  const right = left + w / zoom;
  const bottom = top + h / zoom;

  // Extend one extra cell on each side so lines don't pop in/out at edges
  const startX = Math.floor((left - gridSize) / gridSize) * gridSize;
  const endX = Math.ceil((right + gridSize) / gridSize) * gridSize;
  const startY = Math.floor((top - gridSize) / gridSize) * gridSize;
  const endY = Math.ceil((bottom + gridSize) / gridSize) * gridSize;

  ctx.save();
  // Apply retina + viewport transform so we draw in canvas-space coordinates
  ctx.setTransform(
    retina * vpt[0],
    retina * vpt[1],
    retina * vpt[2],
    retina * vpt[3],
    retina * vpt[4],
    retina * vpt[5],
  );
  ctx.strokeStyle = gridColor;
  ctx.globalAlpha = gridOpacity;
  ctx.lineWidth = 1 / zoom; // always 1px on screen regardless of zoom

  ctx.beginPath();
  for (let x = startX; x <= endX; x += gridSize) {
    ctx.moveTo(x, startY);
    ctx.lineTo(x, endY);
  }
  for (let y = startY; y <= endY; y += gridSize) {
    ctx.moveTo(startX, y);
    ctx.lineTo(endX, y);
  }
  ctx.stroke();
  ctx.restore();
}
